import {
  ApplePayPaymentRequest,
  Client,
  GooglePay,
  GooglePayConfig,
  TokenizeApplePayResult,
  TokenizeCardConnectGooglePayResult,
  util,
  tokenize
} from '@idonatedev/idonate-sdk';
import { PaymentMethodComponent } from '../shared/forms/payment-method.component';
import {
  BillingInfo,
  BillingInfoActions
} from '../transaction-store/billing-info';
import { Donation } from '../transaction-store/donation';
import { OptIns } from '../transaction-store/opt-ins';
import { Injectable } from '@angular/core';
import { AppSettings } from '../app.settings';
import { environment } from '../../environments/environment';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { APP_NAME } from '../constants';
import { isMobile } from 'app/shared/utils/isMobile';

@Injectable()
// tslint:disable-next-line:class-name
export class Sdk {
  public readonly client: Client;
  public applePayResponse: Omit<TokenizeApplePayResult, 'billingContact'> & {
    billingContact: BillingInfo;
  };
  public googlePayResponse: {
    button: HTMLElement | null;
    cardConnectResponse: TokenizeCardConnectGooglePayResult;
    cardDescription: string;
  };
  private googlePay: GooglePay;
  private billingState: BillingInfo;
  private donation: Donation;
  public activePaymentComponent: PaymentMethodComponent | null = null;
  @Select() donation$: Observable<Donation>;
  @Select() billingInfo$: Observable<BillingInfo>;
  constructor(private appSettings: AppSettings, private store: Store) {
    this.client = new Client(
      appSettings.getValue('organization_id'),
      appSettings.getValue('id'), // embedId
      { ...environment.sdkClientConfig, sdkClientName: 'single-page-embed' }
    );

    const sdkInitPayload = {
      embed_id: this.appSettings.getValue('id'),
      action: 'initSDK',
      scope: 'sdk',
      params: {
        sdkConfig: {
          ...environment.sdkClientConfig,
          headers: {
            ...environment.sdkClientConfig.headers,
            sdkClientName: APP_NAME
          }
        }
      }
    };

    this.messageiDonateLib(sdkInitPayload);

    this.initGooglePay();

    this.billingInfo$.subscribe(billingState => {
      this.billingState = billingState;
    });

    this.donation$.subscribe(donation => {
      this.donation = donation;
    });

  }

  private messageiDonateLib(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const channel = new MessageChannel();

      channel.port1.onmessage = ({ data }) => {
        const response = JSON.parse(data);
        if (response.error) {
          reject(response.error);
        } else {
          resolve(response.result);
        }
      };
      window.parent.postMessage(JSON.stringify(payload), '*', [channel.port2]);
    });
  }

  public async isApplePaySupported() {
    const payload = {
      embed_id: this.appSettings.getValue('id'),
      action: 'isApplePaySupported',
      scope: 'sdk'
    };
    const response = await this.messageiDonateLib(payload);
    return response.isSupported;
  }

  /**
   * Whether the host page's Apple Pay can yield a merchant token for a
   * recurring gift (API 14+). Answered by idonatelib on the host page; an
   * idonatelib without this action never replies, which leaves recurring
   * hidden for Apple Pay, the same as before it existed.
   */
  public async isApplePayRecurringSupported(): Promise<boolean> {
    const payload = {
      embed_id: this.appSettings.getValue('id'),
      action: 'isApplePayRecurringSupported',
      scope: 'sdk'
    };
    const response = await this.messageiDonateLib(payload);
    return !!response.isSupported;
  }

  public async initApplePay(
    paymentConfig: Partial<ApplePayPaymentRequest>
  ) {
    const payload = {
      embed_id: this.appSettings.getValue('id'),
      action: 'initApplePay',
      scope: 'sdk',
      params: { paymentConfig, sdkConfig: environment.sdkClientConfig }
    };
    const response = await this.messageiDonateLib(payload);
    const contact = response.billingContact;

    const billingContact = {
      firstName: contact.givenName,
      lastName: contact.familyName,
      middleName: null,
      company: null,
      email: contact.email,
      phone: contact.phone,
      address1: contact.addressLines[0],
      address2: contact.addressLines[1],
      city: contact.locality,
      state: contact.administrativeArea,
      zip: contact.postalCode,
      country: contact.country,
      title: null
    };

    this.applePayResponse = Object.assign(response, { billingContact });
  }

  public completeApplePayPayment(status: number) {
    const payload = {
      embed_id: this.appSettings.getValue('id'),
      action: 'completeApplePayPayment',
      scope: 'sdk',
      params: { status }
    };
    this.messageiDonateLib(payload);
  }

  private initGooglePay() {
    this.googlePayResponse = {
      button: null,
      cardConnectResponse: null,
      cardDescription: ''
    };

    const paymentGateway = this.appSettings.getValue('digitalWalletGateway');
    const env = environment.sdkClientConfig.enableSandboxMode
      ? 'TEST'
      : 'PRODUCTION';

    if (!paymentGateway) {
      return;
    }

    const googlePayConfig: GooglePayConfig = {
      paymentDataRequest: {
        merchantInfo: {
          // a merchant ID is available for a production environment after approval from Google
          // See {@link https://developers.google.com/pay/api/web/guides/test-and-deploy/integration-checklist|Integration checklist}
          merchantId: environment.sdkClientConfig.googlePayMerchantId,
          merchantName: this.appSettings.getValue('organization').name
        },
        emailRequired: true,
        shippingAddressRequired: false,
      },
      paymentOptions: {
        environment: env as GooglePayConfig['paymentOptions']['environment']
      },
      cardConnectMerchantId:
        paymentGateway.backendName === 'card_connect'
          ? paymentGateway.merchantId
          : null
    } as any;

    this.googlePay = new GooglePay(googlePayConfig);
    GooglePay.injectScript();
    this.googlePay.getGooglePaymentClient().then(client => {
      this.googlePayResponse.button = client.createButton({
        onClick: () => {
          this.handleGooglePay();
        },
        buttonType: 'plain',
        buttonSizeMode: 'fill'
      });
    });
  }

  private handleGooglePay() {
    const optIns: OptIns = this.store.selectSnapshot(state => state.optIns);
    const feeAmount = optIns.payFee
      ? parseFloat(optIns.feeAmount.toFixed(2))
      : 0;
    const transactionInfo = {
      countryCode: this.billingState.country,
      currencyCode: this.appSettings.getValue('organization').currency_code,
      totalPrice: (this.donation.amount + feeAmount).toString()
    };

    this.googlePay
      .onGooglePaymentButtonClicked(transactionInfo)
      .then(googlePayResult => {
        const paymentToken =
          googlePayResult.paymentMethodData.tokenizationData.token;
        const billingData =
          googlePayResult.paymentMethodData.info.billingAddress;
        const nameObj = util.splitName(billingData.name);
        const billingContact = {
          firstName: nameObj.firstName,
          lastName: nameObj.lastName,
          middleName: null,
          company: null,
          email: googlePayResult.email,
          phone: billingData.phoneNumber,
          address1: billingData.address1,
          address2: billingData.address2,
          city: billingData.locality,
          state: billingData.administrativeArea,
          zip: billingData.postalCode,
          country: billingData.countryCode,
          title: null
        };
        this.googlePayResponse.cardDescription =
          googlePayResult.paymentMethodData.description;
        this.store.dispatch(
          new BillingInfoActions.UpdateBillingInfo(billingContact)
        );
        return this.googlePay.tokenizeWithCardConnect(
          paymentToken,
          this.client.config.cardConnectBaseUrl
        );
      })
      .then(cardConnectToken => {
        this.googlePayResponse.cardConnectResponse = cardConnectToken;
      })
      .catch(err => {
        console.error('Google Pay tokenization failed:', err);
        this.googlePayResponse.cardConnectResponse = null;
      });
  }

  public async isGooglePaySupported() {
    const client = await this.googlePay.getGooglePaymentClient();
    return client.isReadyToPay(this.googlePay.getGoogleIsReadyToPayRequest());
  }

  public trackEmbedVisit(embedId, organizationId, campaignId, visitorId) {
    const trackVisitUrl = `${this.client.config.apiBaseUrl}/embed/metrics/visit/${embedId}`;

    fetch(trackVisitUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        visitor_id: visitorId,
        organization_id: organizationId,
        campaign_id: campaignId,
        variant: -1,
        version: -1,
        is_mobile: isMobile()
      })
    }).then(); // do nothing with response
  }

  /**
   * Create a unified tokenizer for credit card or bank account
   */
  public async createTokenizer(
    mode: 'credit_card' | 'bank_account',
    containerId: string
  ): Promise<tokenize.Tokenizer> {
    // Get payment gateway configuration
    const paymentGateway = this.getPaymentGateway(mode);

    try {
      // Create tokenizer with unified styling
      // organizationId and embedId are already in the client
      const tokenizer = await this.client.createTokenizer(paymentGateway, {
        containerId,
        mode,
        styling: this.getSinglePageEmbedStyles()
      });

      return tokenizer;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Clean up active payment component reference
   */
  public clearActivePaymentComponent(): void {
    this.activePaymentComponent = null;
  }

  /**
   * Get payment gateway configuration based on mode
   */
  private getPaymentGateway(mode: 'credit_card' | 'bank_account') {
    const gateway =
      mode === 'credit_card'
        ? this.appSettings.getValue('paymentGateway')
        : this.appSettings.getValue('achGateway') ||
          this.appSettings.getValue('paymentGateway');

    return {
      id: gateway.id,
      backendName: gateway.backendName,
      name: gateway.name,
      config: {
        environmentKey:
          gateway.backendName === 'spreedly'
            ? gateway.spreedlyEnvironmentKey ||
              environment.sdkClientConfig.spreedlyEnvironmentKey
            : undefined,
        baseUrl:
          gateway.backendName === 'card_connect'
            ? gateway.baseUrl || this.client.config.cardConnectBaseUrl
            : undefined,
        merchantId: gateway.merchantId
      }
    };
  }

  /**
   * Get single page embed styling configuration
   */
  private getSinglePageEmbedStyles(): any {
    return {
      input: {
        height: '46px',
        padding: '12px 14px',
        fontSize: '14px',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        border: '1px solid #b6b8ba',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        color: '#374151',
        lineHeight: '1.5'
      },
      focus: {
        borderColor: '#3b82f6',
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
      },
      error: {
        borderColor: '#ef4444',
        backgroundColor: '#fef2f2'
      },
      container: {
        display: 'flex',
        gap: '0',
        alignItems: 'stretch'
      }
    };
  }
}
